import React, { useState, useMemo, useEffect } from 'react';
import { Field } from '@api';
import { getRowValues, getDocumentIcon, isAmountField, isNumericField, isDateField, formatCell, DEFAULT_LOCALE } from './seachpage-helpers';
import { ActionMenu } from './seachpage-actionmenu';
import { DocumentPreviewModal } from './seachpage-preview';
import { getDocumentUri, http } from '@api';
import { FaFolder } from 'react-icons/fa';

export interface DocumentGridProps {
  documents: any[];
  fields: Field[];
  documentTypes: any[];
  resultColumns: string[];
  onColumnToggle?: (col: string) => void;
  showColumnSelector?: boolean;
  onToggleColumnSelector?: () => void;
  sortField?: string | null;
  sortDir?: 'asc' | 'desc';
  onHeaderClick?: (col: string) => void;
  onEdit?: (doc: any) => void;
  onAudit?: (doc: any) => void;
  onMetadata?: (doc: any) => void;
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  emptyMessage?: string;
  folders?: { id: number; name: string }[];
  onFolderOpen?: (folderId: number) => void;
  loadingFolders?: boolean;
  foldersHasMore?: boolean;
  onLoadMoreFolders?: () => void;
  loadingFields?: boolean;
  drawerId?: number | null; // new: identify which drawer for persisting settings
  storagePrefix?: string; // optional prefix to separate storage keys per screen (e.g., 'search' or 'browse')
  onResetColumns?: () => void;
  onPageSizeChange?: (size: number) => void;
}

type MetadataState = {
  loading: boolean;
  error: string | null;
  rows: Array<Record<string, any>> | null;
};

function displayTitle(fld: Field | undefined, key: string) {
  if (fld?.title) return fld.title;
  const k = (key || '').toLowerCase();
  if (k === 'description' || k === 'document_description' || k === 'documentdescription') return 'Description';
  if (k === 'createdate' || k === 'documentdatecreated') return 'Create Date';
  if (k === 'documentid' || k === 'id' || k === 'docid') return 'Document Id';
  if (k === 'subfolder') return 'Subfolder';
  return key;
}

export const DocumentGrid: React.FC<DocumentGridProps> = ({
  documents,
  fields,
  documentTypes,
  resultColumns,
  onColumnToggle,
  showColumnSelector = false,
  onToggleColumnSelector,
  sortField,
  sortDir,
  onHeaderClick,
  onEdit,
  onAudit,
  onMetadata,
  loading = false,
  loadingMore = false,
  onLoadMore,
  hasMore = false,
  emptyMessage = 'No documents found.',
  folders = [],
  onFolderOpen,
  loadingFolders = false,
  foldersHasMore = false,
  onLoadMoreFolders,
  loadingFields = false,
  drawerId = null,
  storagePrefix = 'grid',
  onResetColumns,
  onPageSizeChange,
}) => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{ uri: string; extension: string; documentType?: string } | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [metaCache, setMetaCache] = useState<Map<number, MetadataState>>(() => new Map());

  // local column settings state
  const [localOrder, setLocalOrder] = useState<string[] | null>(null); // null = not loaded
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => new Set());
  const [showModal, setShowModal] = useState(false);
  const [modalPos, setModalPos] = useState<{ left: number; top: number }>({ left: 100, top: 100 });
  const draggingRef = React.useRef(false);
  const dragOffsetRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // keys for persistence per drawer and per storagePrefix (separate search vs browse)
  const pref = storagePrefix || 'grid';
  const orderKey = `${pref}_cols_order_${drawerId ?? 'none'}`;
  const hiddenKey = `${pref}_cols_hidden_${drawerId ?? 'none'}`;
  const pageSizeKey = `${pref}_page_size_${drawerId ?? 'none'}`;
  const [pageSize, setPageSize] = useState<number>(() => {
    try { const raw = localStorage.getItem(pageSizeKey); const v = raw ? Number(raw) : 50; return Number.isFinite(v) && v > 0 ? v : 50; } catch { return 50; }
  });

  // Persist and notify parent when pageSize changes
  React.useEffect(() => {
    try { localStorage.setItem(pageSizeKey, String(pageSize)); } catch {}
    try { if (onPageSizeChange) onPageSizeChange(pageSize); } catch {}
  }, [pageSize, pageSizeKey, onPageSizeChange]);

  // internal sorting fallback if parent doesn't provide onHeaderClick
  const [internalSortField, setInternalSortField] = useState<string | null>(null);
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (showColumnSelector && onToggleColumnSelector) onToggleColumnSelector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showColumnSelector]);

  // Drag listeners for modal
  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current) return;
      const nx = e.clientX - dragOffsetRef.current.x;
      const ny = e.clientY - dragOffsetRef.current.y;
      const left = Math.max(8, Math.min(nx, window.innerWidth - 100));
      const top = Math.max(8, Math.min(ny, window.innerHeight - 40));
      setModalPos({ left, top });
    }
    function onUp() { draggingRef.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  // Build orderedValueKeys local copy similar to previous logic
  const orderedValueKeys = useMemo(() => {
    const fieldNames = fields.map(f => f.name);
    const present = new Set(fieldNames);
    const extras = new Set<string>();

    documents.forEach(d => {
      if (d.documentValues) Object.keys(d.documentValues).forEach(k => { if (!present.has(k)) extras.add(k); });
      const tv = d.topFolder?.folderValues;
      if (tv) Object.keys(tv).forEach(k => { if (!present.has(k)) extras.add(k); });
    });

    const metaKeys = ['description', 'subfolder', 'documentid', 'createdate'];
    metaKeys.forEach(k => { if (!present.has(k)) extras.add(k); });

    const folderNames = fields.filter(f => f.folderfield).map(f => f.name);
    const docNames = fields.filter(f => !f.folderfield).map(f => f.name);

    const ordered: string[] = [];
    folderNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });
    if (ordered.indexOf('subfolder') === -1) ordered.push('subfolder');
    docNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });

    const extraArr = Array.from(extras).sort();
    extraArr.forEach(e => { if (ordered.indexOf(e) === -1) ordered.push(e); });

    ['documentid', 'createdate'].forEach(m => { if (ordered.indexOf(m) === -1) ordered.push(m); });
    fieldNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });

    return ordered;
  }, [documents, fields]);

  // Load saved settings for this drawer
  useEffect(() => {
    try {
      const rawOrder = localStorage.getItem(orderKey);
      const rawHidden = localStorage.getItem(hiddenKey);
      let parsedOrder: string[] | null = null;
      let parsedHidden: string[] | null = null;
      if (rawOrder) {
        const p = JSON.parse(rawOrder);
        if (Array.isArray(p)) parsedOrder = p;
      }
      if (rawHidden) {
        const p = JSON.parse(rawHidden);
        if (Array.isArray(p)) parsedHidden = p;
      }

      if (parsedOrder) {
        // normalize: keep only keys known or present in orderedValueKeys, then append missing keys preserving orderedValueKeys
        const set = new Set(orderedValueKeys);
        const filtered = parsedOrder.filter(c => set.has(c));
        const missing = orderedValueKeys.filter(c => filtered.indexOf(c) === -1);
        setLocalOrder([...filtered, ...missing]);
      } else {
        setLocalOrder(null);
      }

      if (parsedHidden) {
        const setHidden = new Set(parsedHidden.filter(c => orderedValueKeys.indexOf(c) !== -1));
        setHiddenCols(setHidden);
      } else {
        setHiddenCols(new Set());
      }
    } catch {
      setLocalOrder(null);
      setHiddenCols(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey, hiddenKey, drawerId, JSON.stringify(orderedValueKeys)]);

  // Compute final columns order (order stored separately from hidden state)
  const baseOrder = useMemo(() => {
    if (localOrder && localOrder.length) return localOrder.slice();
    if (resultColumns && resultColumns.length) {
      // Ensure entire set present: keep resultColumns order then append others
      const set = new Set(orderedValueKeys);
      const filtered = resultColumns.filter(c => set.has(c));
      const missing = orderedValueKeys.filter(c => filtered.indexOf(c) === -1);
      return [...filtered, ...missing];
    }
    return orderedValueKeys.slice();
  }, [localOrder, resultColumns, orderedValueKeys]);

  const finalColumns = useMemo(() => {
    const res = baseOrder.filter(c => !hiddenCols.has(c));
    return res;
  }, [baseOrder, hiddenCols]);

  // Persist helpers
  function persistOrder(arr: string[]) {
    try { localStorage.setItem(orderKey, JSON.stringify(arr)); } catch {}
  }
  function persistHidden(arr: string[]) {
    try { localStorage.setItem(hiddenKey, JSON.stringify(arr)); } catch {}
  }

  // Modal actions
  function openColumnModal() {
    // center modal initially
    try {
      const w = 420; const h = 320;
      const left = Math.max(8, Math.round((window.innerWidth - w) / 2));
      const top = Math.max(8, Math.round((window.innerHeight - h) / 2));
      setModalPos({ left, top });
    } catch {}
    setShowModal(true);
  }
  function closeColumnModal() { setShowModal(false); }

  // Drag and drop handlers for reordering
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  function onDragStart(e: React.DragEvent, idx: number) {
    e.dataTransfer.setData('text/plain', String(idx));
    // show move cursor
    try { e.dataTransfer.effectAllowed = 'move'; } catch {}
  }
  function onDragOverItem(e: React.DragEvent, idx: number) {
    e.preventDefault();
    setDragOverIndex(idx);
  }
  function onDropItem(e: React.DragEvent, idx: number) {
    e.preventDefault();
    const s = e.dataTransfer.getData('text/plain');
    const from = Number(s);
    const curOrder = baseOrder.slice();
    if (isNaN(from) || from < 0 || from >= curOrder.length) { setDragOverIndex(null); return; }
    const removed = curOrder.splice(from, 1);
    if (removed.length === 0) { setDragOverIndex(null); return; }
    const item = removed[0] as string;
    curOrder.splice(idx, 0, item);
    setLocalOrder(curOrder);
    persistOrder(curOrder);
    setDragOverIndex(null);
  }

  function toggleHidden(col: string) {
    const next = new Set(hiddenCols);
    if (next.has(col)) next.delete(col); else next.add(col);
    setHiddenCols(next);
    persistHidden(Array.from(next));
    if (onColumnToggle) onColumnToggle(col); // notify parent optionally
  }

  // apply sorting: prefer parent-provided handlers; otherwise use internal
  function handleHeaderClick(col: string) {
    if (onHeaderClick) { onHeaderClick(col); return; }
    // internal sort
    if (internalSortField === col) setInternalSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setInternalSortField(col); setInternalSortDir('asc'); }
  }

  const effectiveSortField = onHeaderClick ? sortField : internalSortField;
  const effectiveSortDir = onHeaderClick ? sortDir : internalSortDir;

  const sortedDocuments = useMemo(() => {
    if (!effectiveSortField) return documents;
    const key = effectiveSortField;
    const dir = effectiveSortDir === 'asc' ? 1 : -1;
    const copy = documents.slice();
    copy.sort((a: any, b: any) => {
      const va = (getRowValues(a, [key])[0]);
      const vb = (getRowValues(b, [key])[0]);
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;
      // numeric
      const na = Number(va); const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
      // date
      const da = new Date(va); const db = new Date(vb);
      if (!isNaN(da.getTime()) && !isNaN(db.getTime())) return (da.getTime() - db.getTime()) * dir;
      // string compare
      return String(va).localeCompare(String(vb)) * dir;
    });
    return copy;
  }, [documents, effectiveSortField, effectiveSortDir]);

  function toggleSelect(id: number, checked?: boolean) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked === undefined) {
        if (next.has(id)) next.delete(id); else next.add(id);
      } else {
        if (checked) next.add(id); else next.delete(id);
      }
      return next;
    });
  }

  function selectAll(checked: boolean) {
    if (checked) {
      const allIds = documents.map(d => d.id ?? d.Id).filter(Boolean) as number[];
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  }

  function getFieldByName(name: string): Field | undefined {
    return fields.find(f => f.name === name);
  }

  function getDocumentTypeObject(dtId: any) {
    return documentTypes.find(dt => dt.id === dtId);
  }

  const totalCols = (finalColumns?.length || 0) + 3;

  async function resolveDocumentUri(doc: any, download = false): Promise<string | null> {
    if (doc.uri || doc.Uri) return doc.uri || doc.Uri;
    try {
      const uri = await getDocumentUri(doc.id, download);
      return uri || null;
    } catch {
      return null;
    }
  }

  async function downloadDocument(doc: any, ext: string) {
    const uri = await resolveDocumentUri(doc, true);
    if (!uri) return;
    const filename = doc.filename || doc.name || (`document_${doc.id ?? doc.Id ?? 'file'}.${ext || 'bin'}`);
    try {
      const resp = await fetch(uri);
      if (!resp.ok) throw new Error('Failed to fetch file for download');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      // fallback: open in new tab
      window.open(uri, '_blank');
    }
  }

  async function handleOpenOrPreview(e: React.MouseEvent, doc: any, ext: string, typeName: string) {
    e.stopPropagation();

    // modifier keys -> immediate download
    if ((e as any).shiftKey || (e as any).metaKey) {
      await downloadDocument(doc, ext);
      return;
    }


    const uri = await resolveDocumentUri(doc, false);
    if (!uri) return;
    const previewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif'].indexOf((ext || doc.extension || '').toLowerCase()) !== -1;
    if (previewable) {
      setPreviewDoc({ uri, extension: ext, documentType: typeName });
    } else {
      // non-previewable: attempt download
      await downloadDocument(doc, ext);
    };
  }

  async function toggleRow(doc: any) {
    // If a recent menu click closed the popup, suppress this row click expansion
    try {
      if ((window as any).__suppressNextRowClick) { delete (window as any).__suppressNextRowClick; return; }
    } catch {}
    const id = doc.id ?? doc.Id;
    const next = new Set(expanded);
    if (next.has(id)) {
      next.delete(id);
      setExpanded(next);
      return;
    }
    next.add(id);
    setExpanded(next);

    if (!metaCache.has(id)) {
      setMetaCache(prev => new Map(prev).set(id, { loading: true, error: null, rows: null }));
      try {
        const data = await http(`/api/document/DocumentRoot/Metadata?documentId=${id}`);
        let rows: Array<Record<string, any>> = [];
        if (Array.isArray(data)) rows = data as Array<Record<string, any>>;
        else if (data && typeof data === 'object') rows = [data as Record<string, any>];
        setMetaCache(prev => new Map(prev).set(id, { loading: false, error: null, rows }));
      } catch (e: any) {
        setMetaCache(prev => new Map(prev).set(id, { loading: false, error: e?.message || 'Failed to load metadata', rows: [] }));
      }
    }
  }

  function renderInlineMetadata(id: number) {
    const state = metaCache.get(id);
    if (!state) return null;
    if (state.loading) return <div className="inline-meta-loading">Loading metadata…</div>;
    if (state.error && (!state.rows || state.rows.length === 0)) return <div className="inline-meta-error">{state.error}</div>;

    const rows = state.rows || [];
    if (!rows.length) return <div className="inline-meta-empty">No metadata.</div>;

    const columns: string[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
      const keys = Object.keys(r || {});
      for (const k of keys) {
        if (!k || k.toLowerCase() === 'codingid') continue;
        if (!seen.has(k)) { seen.add(k); columns.push(k); }
      }
    }

    const formatVal = (val: any, colName?: string) => {
      if (val == null) return '';
      if (colName && colName.toLowerCase() === 'createdate' && typeof val === 'string') {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d.toLocaleString(DEFAULT_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
      if (typeof val === 'number') return val.toString();
      if (typeof val === 'boolean') return val ? 'true' : 'false';
      if (Array.isArray(val)) return val.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    };

    return (
      <div className="inline-meta-wrap">
        <div className="inline-meta-table-wrap">
          <table className="inline-meta-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col} className="inline-meta-th">{displayTitle(getFieldByName(col), col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx} className={rIdx % 2 === 0 ? 'inline-meta-row' : 'inline-meta-row alt'}>
                  {columns.map(col => {
                    const raw = (row as any)[col];
                    const txt = formatVal(raw, col);
                    return (
                      <td key={col} className="inline-meta-td">{txt}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="docgrid-root">
      <div className="docgrid-header">
        <h3>Results</h3>
        {onToggleColumnSelector && (
          <button type='button' onClick={() => { openColumnModal(); if (onToggleColumnSelector) onToggleColumnSelector(); }} className="col-selector-toggle" title={showColumnSelector ? 'Hide Column Selector' : 'Show Column Selector'}>
            {showColumnSelector ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 10C2 10 4.5 5 10 5C15.5 5 18 10 18 10C18 10 15.5 15 10 15C4.5 15 2 10 2 10Z" stroke="#4e6e8e" strokeWidth="2"/><path d="M6 6L14 14" stroke="#4e6e8e" strokeWidth="2"/></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 10C2 10 4.5 5 10 5C15.5 5 18 10 18 10C18 10 15.5 15 10 15C4.5 15 2 10 2 10Z" stroke="#4e6e8e" strokeWidth="2"/><circle cx="10" cy="10" r="3" stroke="#4e6e8e" strokeWidth="2"/></svg>
            )}
          </button>
        )}
        {/* Page size selector - right aligned */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: '#556' }} htmlFor="pageSizeSelect">Page</label>
          <select id="pageSizeSelect" value={pageSize} onChange={e=> setPageSize(Number(e.target.value))} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cfd8dc', background: '#fff', cursor: 'pointer' }}>
            {[50,250,500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Column settings modal (drag reorder + show/hide). Uses escaped unicode for drag handle */}
      {showModal && (
        <div className="col-settings-modal">
          <div
            className="col-settings-backdrop"
            onClick={closeColumnModal}
            style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1990 }}
          />
          <div
            className="col-settings-panel"
            style={{ position: 'fixed', left: modalPos.left, top: modalPos.top, width: 420, zIndex: 2000, backgroundColor: '#fff', borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="col-settings-header"
              style={{ cursor: 'move', padding: '8px 12px', borderBottom: '1px solid #ddd' }}
              onMouseDown={(e) => {
                e.preventDefault();
                draggingRef.current = true;
                dragOffsetRef.current = { x: e.clientX - modalPos.left, y: e.clientY - modalPos.top };
              }}
            >
              <strong>Configure Columns</strong>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto', padding: 8 }}>
              <div className="col-settings-list">
                {(baseOrder).map((col, idx) => {
                  const f = getFieldByName(col);
                  const label = displayTitle(f, col);
                  const hidden = hiddenCols.has(col);
                  return (
                    <div
                      key={col}
                      className={`col-settings-item ${dragOverIndex === idx ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, idx)}
                      onDragOver={(e) => onDragOverItem(e, idx)}
                      onDrop={(e) => onDropItem(e, idx)}
                      style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}
                    >
                      <span style={{ marginRight: 8 }}>{'\u2630'}</span>
                      <label style={{ flex: 1, userSelect: 'none' }}>
                        <input type="checkbox" checked={!hidden} onChange={() => toggleHidden(col)} />
                        <span style={{ marginLeft: 8 }}>{label}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="col-settings-actions" style={{ padding: 8, textAlign: 'right' }}>
              <button type="button" onClick={() => {
                try {
                  // remove persisted order and hidden settings so we fall back to server/default order
                  localStorage.removeItem(orderKey);
                  localStorage.removeItem(hiddenKey);
                  // also remove legacy/search page hidden key (e.g. 'searchFieldHidden_{drawer}') if present
                  try { localStorage.removeItem(`${pref}FieldHidden_${drawerId ?? 'none'}`); } catch {}
                } catch {}
                // reset local state
                setLocalOrder(null);
                setHiddenCols(new Set());
                // notify parent so it can also clear its saved settings and recompute defaults
                try { if (onResetColumns) onResetColumns(); } catch {}
              }} className="btn">Reset</button>
              <button type="button" onClick={closeColumnModal} className="btn btn-primary" style={{ marginLeft: 8 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="docgrid-scroll">
        {loadingFields && fields.length === 0 ? (
          <div>Loading...</div>
        ) : (sortedDocuments.length > 0 || folders.length > 0) ? (
          <table className="docgrid-table">
            <thead>
              <tr>
                <th className="docgrid-th-select"><input type="checkbox" onChange={e => selectAll((e.target as HTMLInputElement).checked)} checked={selectedIds.size > 0 && selectedIds.size === documents.length} /></th>
                <th className="docgrid-th-icon" />
                {finalColumns.map(key => {
                  const f = getFieldByName(key);
                  const sorted = effectiveSortField === key;
                  return (
                    <th key={key} onClick={() => handleHeaderClick(key)} className={`docgrid-thead-th ${isDateField(f) ? 'nowrap' : ''}`}>
                      {displayTitle(f, key)}{sorted ? (effectiveSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
                <th className="docgrid-th-actions" />
              </tr>
            </thead>
            <tbody>
              {folders.map(f => (
                <tr key={`folder-${f.id}`} className="docgrid-folder-row">
                  <td className="docgrid-folder-icon-cell">
                    <button type='button' className="docgrid-folder-open-button" title='Open folder' onClick={(e) => { e.stopPropagation(); onFolderOpen?.(f.id); }}>
                      <FaFolder size={18} color="#4e6e8e" />
                    </button>
                  </td>
                  <td colSpan={finalColumns.length + 2} className="docgrid-folder-name-cell" onClick={() => onFolderOpen?.(f.id)}>{f.name}</td>
                </tr>
              ))}

              {foldersHasMore && !loadingFolders && onLoadMoreFolders && (
                <tr>
                  <td colSpan={totalCols} className="docgrid-load-more-cell">
                    <button type='button' onClick={onLoadMoreFolders} className="docgrid-load-more-button">Load more folders</button>
                  </td>
                </tr>
              )}

              {sortedDocuments.map((doc: any, i) => {
                const dtObj = getDocumentTypeObject(doc.documentTypeId);
                const typeName = dtObj?.documentTypeName;
                let ext = dtObj?.documentTypeExtension || '';
                if (ext.startsWith('.')) ext = ext.slice(1);
                const previewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif'].indexOf((ext || doc.extension || '').toLowerCase()) !== -1;
                const id = doc.id ?? i;
                const isExpanded = expanded.has(doc.id ?? doc.Id ?? i);

                return (
                  <React.Fragment key={id}>
                    <tr onClick={() => toggleRow(doc)} className="docgrid-row">
                      <td className="docgrid-select-cell">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(doc.id ?? doc.Id ?? i)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { toggleSelect(doc.id ?? doc.Id ?? i, e.target.checked); }}
                        />
                      </td>
                      <td className="docgrid-icon-cell">
                        <button
                          type='button'
                          className="docgrid-preview-button"
                                    title={'Click to view, hold shift or meta key to download'}
                          onClick={(e) => handleOpenOrPreview(e, doc, ext, typeName)}
                        >
                          {getDocumentIcon(ext || doc.extension || '')}
                        </button>
                      </td>
                      {finalColumns.map(k => {
                        const f = getFieldByName(k);
                        const raw = getRowValues(doc, [k])[0];
                        let formatted: any = formatCell(f, raw);
                        if ((!f && k.toLowerCase() === 'createdate') && typeof raw === 'string') {
                          const d = new Date(raw);
                          if (!isNaN(d.getTime())) formatted = d.toLocaleString(DEFAULT_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
                        }
                        const right = isAmountField(f) || isNumericField(f) || isDateField(f);
                        return (
                          <td key={k} className={`docgrid-td ${right ? 'right' : ''} ${isDateField(f) ? 'nowrap' : ''}`}>
                            {formatted?.toString?.() ?? ''}
                          </td>
                        );
                      })}
                      <td className={`docgrid-action-cell ${openMenuId ? 'disabled' : ''}`}>
                        {(onEdit || onAudit) && (
                          <ActionMenu
                            doc={doc}
                            openMenuId={openMenuId}
                            setOpenMenuId={setOpenMenuId}
                            onEdit={onEdit || (() => {})}
                            onAudit={onAudit || (() => {})}
                            fields={fields}
                            resultColumns={finalColumns}
                            selectedIds={Array.from(selectedIds)}
                            visibleIds={documents.map(d => Number(d.id ?? d.Id)).filter(n => !isNaN(n))}
                          />
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={totalCols} className="docgrid-expanded-row">{renderInlineMetadata(doc.id ?? doc.Id ?? i)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {loadingMore && (
                <tr>
                  <td colSpan={finalColumns.length + 2} className="docgrid-loadingmore-row">Loading more...</td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <div className="docgrid-empty">{emptyMessage}</div>
        )}

        {hasMore && !loadingMore && !loading && onLoadMore && (
          <div className="docgrid-loadmore-area">
            <button type='button' onClick={onLoadMore} className="docgrid-loadmore-button">Load more</button>
          </div>
        )}
      </div>

      <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};